import React, { useState, useEffect, FC } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useParams } from "react-router-dom";
import { isNull, omitBy, map, isEmpty, isNil } from "lodash";
import { decode } from "Helpers/enc";
import errorHandler from "Utils/error-handler";
import { RateTableCreateSchema } from "./rate-table-form-validation";
import "./rate-table-form.scss";
import useQuery from "Hooks/query.hook";
import useSession from "Hooks/session.hook";
import { Col, Row } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import {
  AlertBox,
  Button,
  DateRange,
  TextInput,
  SelectInput,
  TimePicker,
} from "@life-line-lab/components-react";
import { OrderSettingsEnum, SurchargeTypeEnums } from "Data/settings";
import PillRadioInput from "Components/shared/pill-radio-input/pill-radio-input";

const initialInput = {
  locations: null,
  validFrom: {
    date: moment(),
    dateString: moment().toISOString(),
  },
  validTo: {
    date: moment("31/12/9999", "DD/MM/YYYY"),
    dateString: moment("31/12/9999", "DD/MM/YYYY").toISOString(),
  },
  sst: "8",
  rates: [
    {
      weightFrom: "",
      weightTo: "",
      priceBeforeSST: "",
      priceAfterSST: "",
      // markupType: "",
      markupValue: "",
      markupBeforeSST: "",
      markupAfterSST: "",
    },
    {
      weightFrom: "",
      weightTo: "",
      priceBeforeSST: "",
      priceAfterSST: "",
      // markupType: "",
      markupValue: "",
      markupBeforeSST: "",
      markupAfterSST: "",
    },
  ],
  surchargeType: SurchargeTypeEnums.DEFAULT,
};

const initialError = {};

interface HiddenLabelProps {
  labelName: string;
}

/**
 * A component for hidden labels
 * @param labelName The label
 * @returns
 */
const HiddenLabel: FC<HiddenLabelProps> = ({ labelName }) => {
  const { t } = useTranslation();

  return (
    <Col xs={24} sm={24} md={0} className="mb-0">
      <label className="inp-label inp-label--required">
        {t(`label.${labelName}`)}
      </label>
    </Col>
  );
};

/**
 * A component for customer rate table
 * @param props The form props
 * @returns
 */
const CustomerDiscountAndMarkup: FC<any> = (props) => {
  const { t } = useTranslation();

  const { input, handleChange, handleSelectChange, isView } = props || {};

  const pillOptions = [
    { label: t("option.none"), value: SurchargeTypeEnums.DEFAULT },
    { label: t("option.discount"), value: SurchargeTypeEnums.DISCOUNT },
    { label: t("option.markup"), value: SurchargeTypeEnums.MARKUP },
  ];

  const pillProps = {
    onChange: handleSelectChange,
    name: "surchargeType",
    options: pillOptions,
    value: input?.surchargeType,
    label: t("label.modifier"),
    isReadonly: isView,
  };

  return (
    <div className="rtf-customer-discount">
      <div className="rtf-row">
        <div className="rtf-cd-header">
          <div className="rtf-cd-title">{t("title.discountAndMarkup")}</div>
          <div className="rtf-cd-subtitle">
            {t("txt.thisIsAppliedForCustomerAndLocationNotRequired")}
          </div>
        </div>
        <div className="rtf-cd-select">
          <PillRadioInput {...pillProps} />
        </div>
        {input.surchargeType !== SurchargeTypeEnums.DEFAULT && (
          <Row gutter={[16, 0]}>
            <Col span={6}>
              <TextInput
                name="surchargeFixedAmount"
                label={t("label.fixedAmount")}
                onChange={handleChange}
                value={input?.surchargeFixedAmount}
                prefix="RM"
                isReadonly={isView}
              />
            </Col>
            <Col span={6}>
              <TextInput
                name="surchargePercentage"
                label={t("label.percentage")}
                onChange={handleChange}
                value={input?.surchargePercentage}
                suffix="%"
                isReadonly={isView}
              />
            </Col>
            <Col span={6}>
              <TextInput
                name="surchargeCapAmount"
                label={t("label.capAmount")}
                onChange={handleChange}
                value={input?.surchargeCapAmount}
                prefix="RM"
                isReadonly={isView}
              />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

/**
 * Functions and states for the rate table
 * @returns All functions and states
 */
const useRateTableFormComponent = () => {
  const type = useQuery().get("type");

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);

  const isView = type == "view";
  const sstAsValue = 1 + input.sst / 100;

  const handleRowInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    i: string,
  ) => {
    if (isView) {
      return;
    }
    const { name, value } = e.currentTarget;
    let rateList = input.rates;
    let newValues = JSON.parse(JSON.stringify(rateList));
    newValues[i][name] = value;
    setInput({ ...input, rates: newValues });
    setError(initialError);
  };

  const handleRowSelectChange = (e: string | string[], i: string) => {
    if (isView) {
      return;
    }
    let rateList = input.rates;
    let newValues = JSON.parse(JSON.stringify(rateList));
    newValues[i]["markupType"] = e;
    setInput({ ...input, rates: newValues });
    setError(initialError);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setError(initialError);
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value: any, name: string) => {
    setError(initialError);
    setInput((prev) => {
      if (name === "sst" && typeof value === "string") {
        if (isEmpty(value)) {
          // allow empty string
          return { ...prev, [name]: value };
        }

        const isValid = /^(?:1000|[1-9]?\d{1,2}|0)$/.test(value);

        if (!isValid) return prev;
      }

      return { ...prev, [name]: value };
    });
  };

  const handleDateChange = (startDate: any, endDate: any) => {
    if (startDate.date) {
      setError(initialError);
      setInput({ ...input, validFrom: startDate });
    } else if (endDate.date) {
      setError(initialError);
      setInput({ ...input, validTo: endDate });
    }
  };

  const addRows = () => {
    let rates = input.rates.concat({
      weightFrom: "",
      weightTo: "",
      priceBeforeSST: "",
      priceAfterSST: "",
      // markupType: "",
      markupValue: "",
      markupBeforeSST: "",
      markupAfterSST: "",
    });
    setInput({ ...input, ["rates"]: rates });
  };

  const deleteRows = (index) => {
    if (isEmpty(input.rates)) return;

    // filter out with index
    const filtered = input.rates.filter((r, i) => i !== index);
    setError(initialError);
    setInput((prev) => ({ ...prev, rates: filtered }));
  };

  return {
    type,
    input,
    setInput,
    error,
    setError,
    isView,
    sstAsValue,
    handleRowInputChange,
    handleRowSelectChange,
    handleChange,
    handleSelectChange,
    handleDateChange,
    addRows,
    deleteRows,
  };
};

/**
 * Rate Table
 * @notes Flexible for all kinds of rate tables
 */
interface RateTableFormComponentProps {
  info?: any;
  onFetch: any;
  onFetchCustomerList: (val: boolean) => void;
  settingOptions?: any;
  onSubmit?: any;
  onUpdate?: any;
  isCustomer?: boolean;
  settingType: OrderSettingsEnum;
}

const RateTableFormComponent: React.FC<RateTableFormComponentProps> = ({
  info,
  onFetch,
  settingOptions,
  onFetchCustomerList,
  onSubmit,
  onUpdate,
  isCustomer = false,
  settingType = OrderSettingsEnum.RATE_TABLE,
}) => {
  const { org } = useSession();
  const { t } = useTranslation();
  const code = useQuery().get("code");
  const { id } = useParams<{ id: string }>();

  const query = useQuery();

  const encodedCustomerID =
    settingType === OrderSettingsEnum.CUSTOMER_RATE_TABLE
      ? query.get("cOrgID")
      : null;
  const qCustomerOrgID = encodedCustomerID ? decode(encodedCustomerID) : null;

  const encodedLocationCode = query.get("locCode");
  const qLocationCode = encodedLocationCode
    ? decode(encodedLocationCode)
    : null;

  const isNewVersion = query.get("version-create");

  const { customersList = [] } = settingOptions || {};

  const {
    type,
    input,
    setInput,
    error,
    setError,
    isView,
    sstAsValue,
    handleRowInputChange,
    handleRowSelectChange,
    handleChange,
    handleSelectChange,
    handleDateChange,
    addRows,
    deleteRows,
  } = useRateTableFormComponent();

  const markupOption = [
    {
      label: t("label.percentage"),
      value: "PERCENTAGE",
    },
    {
      label: t("label.fixedAmount"),
      value: "FIXED",
    },
  ];

  const areFieldsLocked = !isEmpty(isNewVersion || id) || isView;

  useEffect(() => {
    onFetch({
      orgID: org._id,
      type: OrderSettingsEnum.LOCATION,
      currentVersionOnly: true,
    });

    let newInput = { ...input };

    if (settingType === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
      // if the user is creating a new version or viewing, fetch everything
      // if the user is creating a brand new config, fetch filtered customers
      onFetchCustomerList && onFetchCustomerList(areFieldsLocked);
    }

    if (areFieldsLocked) {
      // locationCode
      if (qLocationCode) {
        newInput = {
          ...newInput,
          locations: qLocationCode,
        };
      }

      if (settingType === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
        // customerOrgID
        if (qCustomerOrgID) {
          newInput = {
            ...newInput,
            customerOrgID: qCustomerOrgID,
          };
        }
      } else {
      }

      setInput && setInput(newInput);
    }

    document.body.classList.add("stack-screen--open");
    return () => document.body.classList.remove("stack-screen--open");
  }, []);

  useEffect(() => {
    if (info) {
      setInput((prev) => ({
        ...prev,
        ...info,
        locations: info.locations,
        validFrom: {
          date: info?.validFrom,
          dateString: moment(info?.validFrom).toISOString(),
        },
        validTo: {
          date: info?.validTo,
          dateString: moment(info?.validTo).toISOString(),
        },
      }));
    }
  }, [info]); // ensures the fields pre-filled when the tab is refreshed, else can set in initialInput

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        settingID: info?._id,
        code: decode(code as any),
        locations: info ? info?.locations : [input?.locations],
        validFrom: moment(input?.validFrom?.date).toISOString(),
        validTo: moment(input?.validTo?.date).toISOString(),
        rates: input.rates.map((rate, i) => {
          return omitBy(
            {
              ...rate,
              weightFrom: input.rates[i - 1]?.weightTo || rate.weightFrom,
              markupValue: rate.markupType ? rate.markupValue : "",
              priceAfterSST: rate.priceBeforeSST
                ? `${(parseFloat(rate.priceBeforeSST) * sstAsValue).toFixed(2)}`
                : "",
              markupBeforeSST:
                rate.markupValue && rate.priceBeforeSST && rate.markupType
                  ? rate.markupType == "FIXED"
                    ? `${(
                        parseFloat(rate.priceBeforeSST) +
                        parseFloat(rate.markupValue)
                      ).toFixed(2)}`
                    : `${(
                        parseFloat(rate.priceBeforeSST) *
                        (1 + parseFloat(rate.markupValue) / 100)
                      ).toFixed(2)}`
                  : "",
              markupAfterSST:
                rate.markupValue && rate.priceBeforeSST && rate.markupType
                  ? rate.markupType == "FIXED"
                    ? `${(
                        parseFloat(rate.priceBeforeSST) * sstAsValue +
                        parseFloat(rate.markupValue)
                      ).toFixed(2)}`
                    : `${(
                        parseFloat(rate.priceBeforeSST) *
                        sstAsValue *
                        (1 + parseFloat(rate.markupValue) / 100)
                      ).toFixed(2)}`
                  : "",
            },
            isEmpty,
          );
        }),
      };

      const value = RateTableCreateSchema.validateSync(
        omitBy(valInput, isNull),
        {
          strict: false,
          abortEarly: true,
          stripUnknown: true,
          context: { isCustomerRequired: isCustomer },
        },
      );

      const finalValue = omitBy(
        {
          ...value,
          locationsData: settingOptions?.LOCATION,
        },
        isNil,
      );

      info ? onUpdate(finalValue) : onSubmit(finalValue);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);

      console.log(path, value);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  const formProps = {
    input,
    setInput,
    error,
    setError,
    handleChange,
    handleSelectChange,
    isView,
  };

  console.log(input);

  return (
    <div className="rate-table-form">
      <form>
        <div className="rate-table-form-inner">
          <div className="main-header">
            <span className="center header-title">
              {t("label.rateTable").toUpperCase()}
            </span>
            <div className="subtitle mt-10">{t("txt.chargeableWeight")}</div>
          </div>
          <div className="rtf-container">
            <div className="rtf-general-container">
              <Row gutter={[16, 0]} style={{ width: "100%" }}>
                {isCustomer && (
                  <Col span={24} lg={6}>
                    <SelectInput
                      showSearch
                      filterOption={(inputValue, option) =>
                        option.label
                          .toLowerCase()
                          .indexOf(inputValue.toLowerCase()) >= 0
                      }
                      isRequired={true}
                      isReadonly={areFieldsLocked}
                      label={t("label.customer")}
                      value={input?.customerOrgID}
                      options={customersList}
                      onChange={(value) => {
                        handleSelectChange(value, "customerOrgID");
                      }}
                    />
                    <AlertBox text={error.customerOrgID} />
                  </Col>
                )}
                <Col span={24} lg={6}>
                  <SelectInput
                    showSearch
                    isRequired={true}
                    isReadonly={!isEmpty(info) || areFieldsLocked}
                    label={t("label.assignToLocation")}
                    placeholder={t("txt.selectHere")}
                    value={input.locations}
                    options={map(
                      settingOptions[OrderSettingsEnum.LOCATION],
                      (location) => {
                        return {
                          label: location?.areaName,
                          value: location?.code,
                        };
                      },
                    )}
                    filterOption={(inputValue, option) =>
                      option.label
                        .toLowerCase()
                        .indexOf(inputValue.toLowerCase()) >= 0
                    }
                    name="locations"
                    onChange={(value) => {
                      handleSelectChange(value, "locations");
                    }}
                  />
                  <AlertBox text={error.locations} />
                </Col>
                <Col span={24} lg={6}>
                  <TextInput
                    name="estimatedDeliveryInDay"
                    label={t("label.estimatedDelivery")}
                    value={input?.estimatedDeliveryInDay}
                    onChange={handleChange}
                    isRequired={true}
                    isReadonly={isView}
                  />
                  <AlertBox text={error?.estimatedDeliveryInDay} />
                </Col>
                <Col span={24} lg={6}>
                  <TimePicker
                    label={t("label.estimatedTimeDelivery")}
                    onChange={(value) => {
                      handleSelectChange(value, "estimatedTime");
                    }}
                    value={input?.estimatedTime}
                    isRequired={true}
                    isReadonly={isView}
                  />
                  <AlertBox text={error.estimatedTime} />
                </Col>
                <Col span={24} lg={6}>
                  <TimePicker
                    label={t("label.cutOffTime")}
                    onChange={(value) => {
                      handleSelectChange(value, "cutOffTime");
                    }}
                    value={input?.cutOffTime}
                    isRequired={true}
                    isReadonly={isView}
                  />
                  <AlertBox text={error.cutOffTime} />
                </Col>
                {!isView ? (
                  <Col span={24} lg={12}>
                    <DateRange
                      isRequired={true}
                      startLabel={t("label.validFrom")}
                      endLabel={t("label.validTo")}
                      value={{
                        startDate: input.validFrom,
                        endDate: input.validTo,
                      }}
                      onChange={handleDateChange}
                    />
                    <AlertBox text={error.validFrom || error.validTo} />
                  </Col>
                ) : (
                  <>
                    <Col span={24} lg={6}>
                      <TextInput
                        label={t("label.validFrom")}
                        value={
                          input.validFrom?.date
                            ? moment(input.validFrom?.date).format("DD-MM-YYYY")
                            : ""
                        }
                        isReadonly={true}
                      />
                    </Col>
                    <Col span={24} lg={6}>
                      <TextInput
                        label={t("label.validTo")}
                        value={
                          input.validTo?.date
                            ? moment(input.validTo?.date).format("DD-MM-YYYY")
                            : ""
                        }
                        isReadonly={true}
                      />
                    </Col>
                  </>
                )}
              </Row>
            </div>
            {settingType === OrderSettingsEnum.CUSTOMER_RATE_TABLE && (
              <>
                <div className="logistics-divider"></div>
                <CustomerDiscountAndMarkup {...formProps} />
              </>
            )}
            {/* {!isView && (
          <Row gutter={[16, 0]} style={{ width: "100%" }}>
            <Col span={24} lg={4}>
              <TextInput
                label={t("label.sst")}
                value={input?.sst}
                onChange={(e) => handleSelectChange(e.target.value, "sst")}
                suffix={"%"}
              />
            </Col>
          </Row>
        )} */}
            <div className="logistics-divider"></div>
            <div className="rtf-general-container">
              <Row gutter={[20, 0]}>
                {/* TODO Make tooltip */}
                <Col xs={24} sm={24} md={24} className="mb-0">
                  <Row gutter={[10, 0]} align="bottom">
                    <Col xs={0} sm={0} md={3} className="mb-0">
                      <label className="inp-label inp-label--required">
                        {t("label.weightFrom")}
                      </label>
                    </Col>
                    <Col xs={0} sm={0} md={3} className="mb-0">
                      <label className="inp-label inp-label--required">
                        {t("label.weightTo")}
                      </label>
                    </Col>
                    <Col xs={0} sm={0} md={3} className="mb-0">
                      <label className="inp-label inp-label--required">
                        {t("label.priceBeforeSST")}
                      </label>
                    </Col>
                    <Col xs={0} sm={0} md={3} className="mb-0">
                      <label className="inp-label">
                        {t("label.priceAfterSST")}
                      </label>
                    </Col>
                    <Col xs={0} sm={0} md={3} className="mb-0">
                      <label className="inp-label">{t("label.markup")}</label>
                    </Col>
                    <Col xs={0} sm={0} md={id ? 3 : 2} className="mb-0">
                      {/* markupValue */}
                    </Col>
                    <Col xs={0} sm={0} md={3} className="mb-0">
                      <label className="inp-label">
                        {t("label.markupBeforeSST")}
                      </label>
                    </Col>
                    <Col xs={0} sm={0} md={3} className="mb-0">
                      <label className="inp-label">
                        {t("label.markupAfterSST")}
                      </label>
                    </Col>
                  </Row>
                  {input.rates?.map((ratesObj, i) => {
                    return (
                      <Row gutter={[10, 0]} key={i}>
                        <HiddenLabel labelName="weightFrom" />
                        <Col span={24} lg={3} className="mb-10">
                          <TextInput
                            isRequired={true}
                            isReadonly={i > 0 || isView}
                            placeholder={t("txt.float")}
                            value={
                              i == 0
                                ? input.rates[i].weightFrom
                                : input.rates[i - 1].weightTo
                            }
                            name="weightFrom"
                            suffix="KG"
                            onChange={(e) => handleRowInputChange(e, i)}
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].weightFrom`]
                                ? t("error.rateTable", {
                                    field: t("label.weightFrom"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        <HiddenLabel labelName="weightTo" />
                        <Col span={24} lg={3} className="mb-10">
                          <TextInput
                            isRequired={true}
                            isReadonly={isView}
                            placeholder={t("txt.float")}
                            value={input.rates[i].weightTo}
                            name="weightTo"
                            suffix="KG"
                            onChange={(e) => handleRowInputChange(e, i)}
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].weightTo`]
                                ? t("error.rateTable", {
                                    field: t("label.weightTo"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        <HiddenLabel labelName="priceBeforeSST" />
                        <Col span={24} lg={3} className="mb-10">
                          <TextInput
                            isRequired={true}
                            isReadonly={isView}
                            placeholder={t("txt.float")}
                            value={input.rates[i].priceBeforeSST}
                            name="priceBeforeSST"
                            onChange={(e) => handleRowInputChange(e, i)}
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].priceBeforeSST`]
                                ? t("error.rateTable", {
                                    field: t("label.priceBeforeSST"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        <HiddenLabel labelName="priceAfterSST" />
                        <Col span={24} lg={3} className="mb-10">
                          <TextInput
                            isRequired={true}
                            isReadonly={true}
                            placeholder={t("txt.float")}
                            value={
                              input.rates[i].priceBeforeSST
                                ? `${(
                                    parseFloat(input.rates[i].priceBeforeSST) *
                                    sstAsValue
                                  ).toFixed(2)}`
                                : ""
                            }
                            name="priceAfterSST"
                            onChange={(e) => handleRowInputChange(e, i)}
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].priceAfterSST`]
                                ? t("error.rateTable", {
                                    field: t("label.priceAfterSST"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        <HiddenLabel labelName="markup" />
                        <Col span={24} lg={3} className="mb-10">
                          <SelectInput
                            showSearch
                            isRequired={true}
                            isReadonly={isView}
                            placeholder={t("txt.selectHere")}
                            value={input.rates[i].markupType}
                            name="markupType"
                            options={markupOption}
                            filterOption={(inputValue, option) =>
                              option.label
                                .toLowerCase()
                                .indexOf(inputValue.toLowerCase()) >= 0
                            }
                            onChange={(e) => handleRowSelectChange(e, i)}
                            allowClear
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].markupType`]
                                ? t("error.rateTable", {
                                    field: t("label.markup"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        <Col span={24} lg={!isView ? 2 : 3} className="mb-10">
                          <TextInput
                            isRequired={
                              input.rates[i].markupType ? true : false
                            }
                            isReadonly={!input.rates[i].markupType || isView}
                            value={input.rates[i].markupValue}
                            name="markupValue"
                            prefix={
                              input.rates[i].markupType === "FIXED"
                                ? "RM"
                                : null
                            }
                            suffix={
                              input.rates[i].markupType === "PERCENTAGE"
                                ? "%"
                                : null
                            }
                            onChange={(e) => handleRowInputChange(e, i)}
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].markupValue`]
                                ? t("error.rateTable", {
                                    field: t("label.markupValue"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        <HiddenLabel labelName="markupBeforeSST" />
                        <Col span={24} lg={3} className="mb-10">
                          <TextInput
                            isRequired={true}
                            isReadonly={true}
                            placeholder={t("txt.float")}
                            value={
                              input.rates[i].priceBeforeSST &&
                              input.rates[i].markupType &&
                              input.rates[i].markupValue
                                ? input.rates[i].markupType == "FIXED"
                                  ? `${(
                                      parseFloat(
                                        input.rates[i].priceBeforeSST,
                                      ) + parseFloat(input.rates[i].markupValue)
                                    ).toFixed(2)}`
                                  : `${(
                                      parseFloat(
                                        input.rates[i].priceBeforeSST,
                                      ) *
                                      (1 +
                                        parseFloat(input.rates[i].markupValue) /
                                          100)
                                    ).toFixed(2)}`
                                : ""
                            }
                            name="markupBeforeSST"
                            onChange={(e) => handleRowInputChange(e, i)}
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].markupBeforeSST`]
                                ? t("error.rateTable", {
                                    field: t("label.markupBeforeSST"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        <HiddenLabel labelName="markupAfterSST" />
                        <Col span={24} lg={3} className="mb-10">
                          <TextInput
                            isRequired={true}
                            isReadonly={true}
                            placeholder={t("txt.float")}
                            value={
                              input.rates[i].priceBeforeSST &&
                              input.rates[i].markupType &&
                              input.rates[i].markupValue
                                ? input.rates[i].markupType == "FIXED"
                                  ? `${(
                                      parseFloat(
                                        input.rates[i].priceBeforeSST,
                                      ) *
                                        sstAsValue +
                                      parseFloat(input.rates[i].markupValue)
                                    ).toFixed(2)}`
                                  : `${(
                                      parseFloat(
                                        input.rates[i].priceBeforeSST,
                                      ) *
                                      sstAsValue *
                                      (1 +
                                        parseFloat(input.rates[i].markupValue) /
                                          100)
                                    ).toFixed(2)}`
                                : ""
                            }
                            name="markupAfterSST"
                            onChange={(e) => handleRowInputChange(e, i)}
                          />
                          <AlertBox
                            text={
                              error[`rates[${i}].markupAfterSST`]
                                ? t("error.rateTable", {
                                    field: t("label.markupAfterSST"),
                                  })
                                : null
                            }
                          />
                        </Col>
                        {/* {id || isView ? (
                          <></>
                        ) : (
                          
                        )} */}
                        {!isView && (
                          <Col md={1} className="mb-10 pt-10">
                            {input?.rates?.length > 1 ? (
                              <div className="text-end">
                                <CloseOutlined onClick={() => deleteRows(i)} />
                              </div>
                            ) : null}
                          </Col>
                        )}
                      </Row>
                    );
                  })}
                </Col>
              </Row>
              {!isView && (
                <Row className="mt-10">
                  <Col xs={24} className="mb-0">
                    <Button
                      size="large"
                      type="primary"
                      onClick={() => addRows()}
                      label={t("action.addRow")}
                      icon={<PlusCircleOutlined />}
                    />
                  </Col>
                </Row>
              )}
            </div>
            {!isView ? (
              <Row className={id ? "mt-40" : "mt-50"} justify="center">
                <Col className="mb-50">
                  <Button
                    size="large"
                    type="primary"
                    onClick={handleSubmit}
                    label={id ? t("action.update") : t("action.create")}
                  />
                </Col>
              </Row>
            ) : null}
          </div>
        </div>
      </form>
    </div>
  );
};

export default RateTableFormComponent;
